import {
  ConnectionHealthRecord,
  ConnectionHealthStatus,
  getAllConnectionHealth,
} from "_shared/_stores/realtime/connection-health-redux-slice";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React from "react";
import { useSelector } from "react-redux";

type SnackbarEventKind = "disconnected" | "reconnecting" | "reestablished";

const EVENT_COOLDOWN_MS = 5000;

function getEventKind(previousStatus: ConnectionHealthStatus, currentStatus: ConnectionHealthStatus): SnackbarEventKind | null {
  if (currentStatus === "disconnected") return "disconnected";
  if (currentStatus === "reconnecting") return "reconnecting";
  if (currentStatus === "connected" && (previousStatus === "reconnecting" || previousStatus === "disconnected")) {
    return "reestablished";
  }

  return null;
}

function getSnackbarConfig(eventKind: SnackbarEventKind) {
  if (eventKind === "disconnected") {
    return {
      message: "Connection to Attorney Hub lost...",
      variant: "error" as const,
    };
  }

  if (eventKind === "reconnecting") {
    return {
      message: "Connection to Attorney Hub lost, reconnecting...",
      variant: "warning" as const,
    };
  }

  return {
    message: "Reconnected to Attorney Hub.",
    variant: "success" as const,
  };
}

function getEventPriority(eventKind: SnackbarEventKind) {
  if (eventKind === "disconnected") return 3;
  if (eventKind === "reconnecting") return 2;
  return 1;
}

export default function ConnectionHealthSnackbarListener() {
  const connections = useSelector(getAllConnectionHealth);
  const previousConnectionsRef = React.useRef<Record<string, ConnectionHealthRecord>>({});
  const lastShownAtRef = React.useRef<Partial<Record<SnackbarEventKind, number>>>({});
  const hasInitializedRef = React.useRef(false);

  React.useEffect(() => {
    const previousConnections = previousConnectionsRef.current;

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousConnectionsRef.current = Object.fromEntries(connections.map((connection) => [connection.key, connection]));
      return;
    }

    let nextEventKind: SnackbarEventKind | null = null;

    for (const connection of connections) {
      const previousConnection = previousConnections[connection.key];
      if (!previousConnection || previousConnection.status === connection.status) continue;

      const eventKind = getEventKind(previousConnection.status, connection.status);
      if (!eventKind) continue;

      if (!nextEventKind || getEventPriority(eventKind) > getEventPriority(nextEventKind)) {
        nextEventKind = eventKind;
      }
    }

    if (nextEventKind) {
      const now = Date.now();
      const lastShownAt = lastShownAtRef.current[nextEventKind];
      if (lastShownAt === undefined || now - lastShownAt >= EVENT_COOLDOWN_MS) {
        const snackbarConfig = getSnackbarConfig(nextEventKind);
        enqueueSnackbar(snackbarConfig.message, {
          key: `${nextEventKind}-${now}`,
          variant: snackbarConfig.variant,
        });
        lastShownAtRef.current[nextEventKind] = now;
      }
    }

    previousConnectionsRef.current = Object.fromEntries(connections.map((connection) => [connection.key, connection]));
  }, [connections]);

  return null;
}
